import { OverlayScrollArea } from '@odo/shared-ui';
import { useMemo } from 'react';
import { useMonitoringVaMix } from '../../../hooks/useMonitoringVaMix';
import type { VaMixTeam } from '../../../services/vaMonitoringOverview';
import type { GranularityOption } from '../../accounts/GranularityToggle';
import { CardErrorState } from '../../accounts/overview/CardErrorState';
import { CompactSegmentedToggle } from '../../billing/overview/CompactSegmentedToggle';
import { OverviewCard } from '../../billing/overview/OverviewCard';
import { SectionToggle } from '../../patient-files/actions/SectionToggle';
import { ChartSkeleton } from '../../shared/ChartSkeleton';
import { VIRTUAL_APPOINTMENT_STATUS_LABELS } from '@odo/shared-ui/clinical/virtualAppointments/summary';
import { virtualAppointmentStatusBg } from '../../virtual-appointments/virtualAppointmentStatusColors';
import { OVER_TIME_CHART_HEIGHT_PX } from './OverTimeChart';
import { EmptyNote, StackedBar, SwatchLegend } from './StackedBar';
import { useStoredChoice } from './useStoredChoice';
import { buildVaMix, type VaMixChart, type VaMixRow } from './vaMixRows';

type PlannedChoice = 'hide' | 'include';

const PLANNED_OPTIONS: GranularityOption<PlannedChoice>[] = [
    { key: 'hide', label: 'Hide planned' },
    { key: 'include', label: 'Include planned' }
];
const TEAM_OPTIONS: GranularityOption<VaMixTeam>[] = [
    { key: 'all', label: 'All' },
    { key: 'DM', label: 'DM' },
    { key: 'Clinical', label: 'Clinical' }
];
const PLANNED_STORAGE_KEY = 'monitoring_overview_va_mix_planned';
const TEAM_STORAGE_KEY = 'monitoring_overview_va_mix_team';
const ROW_GRID = 'grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)_2.5rem] items-center gap-3';

function MixRow({ row, max }: { row: VaMixRow; max: number }) {
    return (
        <div className={`${ROW_GRID} px-2 py-1 text-sm`}>
            <span className="truncate text-gray-700" title={row.label}>
                {row.label}
            </span>
            <StackedBar
                label={row.label}
                total={max}
                percentBase={row.total}
                heightClassName="h-2.5"
                segments={row.segments.map(({ status, count }) => ({
                    key: status,
                    label: VIRTUAL_APPOINTMENT_STATUS_LABELS[status],
                    count,
                    swatch: virtualAppointmentStatusBg(status)
                }))}
            />
            <span className="text-right font-semibold tabular-nums text-gray-900">
                {row.total.toLocaleString()}
            </span>
        </div>
    );
}

function MixRows({ mix }: { mix: VaMixChart }) {
    const max = Math.max(0, ...mix.rows.map((row) => row.total));
    return (
        <div className="flex flex-col gap-3">
            <OverlayScrollArea className="-mx-2" contentClassName="max-h-[200px]">
                <div className="flex flex-col gap-1">
                    {mix.rows.map((row) => (
                        <MixRow key={row.key} row={row} max={max} />
                    ))}
                </div>
            </OverlayScrollArea>
            <SwatchLegend
                items={mix.statuses.map((status) => ({
                    key: status,
                    label: VIRTUAL_APPOINTMENT_STATUS_LABELS[status],
                    swatch: virtualAppointmentStatusBg(status)
                }))}
            />
        </div>
    );
}

interface MixBodyProps {
    mix: VaMixChart | null;
    isLoading: boolean;
    isSwitching: boolean;
    isError: boolean;
    onRetry: () => void;
}

function MixBody({ mix, isLoading, isSwitching, isError, onRetry }: MixBodyProps) {
    if (mix && mix.rows.length > 0) {
        return (
            <div className={`transition-opacity ${isSwitching ? 'opacity-60' : ''}`}>
                <MixRows mix={mix} />
            </div>
        );
    }
    return (
        <div className="grid place-items-center" style={{ height: OVER_TIME_CHART_HEIGHT_PX }}>
            {isLoading ? (
                <div className="h-full w-full">
                    <ChartSkeleton />
                </div>
            ) : isError ? (
                <CardErrorState message="Couldn't load VAs by type" onRetry={onRetry} />
            ) : (
                <EmptyNote>No VAs to show</EmptyNote>
            )}
        </div>
    );
}

export function VaMixCard() {
    const [planned, setPlanned] = useStoredChoice(PLANNED_STORAGE_KEY, PLANNED_OPTIONS);
    const [team, setTeam] = useStoredChoice(TEAM_STORAGE_KEY, TEAM_OPTIONS);
    const includePlanned = planned === 'include';
    const query = useMonitoringVaMix(team);
    const mix = useMemo(
        () => (query.data ? buildVaMix(query.data, includePlanned) : null),
        [query.data, includePlanned]
    );
    return (
        <OverviewCard
            title="VAs by type and status"
            titleClassName="text-[15px]"
            subtitle="Current VAs per type"
            info="Every VA as it stands now, by type and status. Planned VAs haven't been triggered yet."
            wrapAction
            action={
                <div className="flex flex-wrap items-center justify-end gap-3">
                    <label className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500">
                        <SectionToggle
                            size="sm"
                            enabled={includePlanned}
                            onToggle={() => setPlanned(includePlanned ? 'hide' : 'include')}
                        />
                        Include planned
                    </label>
                    <CompactSegmentedToggle
                        options={TEAM_OPTIONS}
                        active={team}
                        onChange={setTeam}
                        ariaLabel="VAs by type: all teams, DM or Clinical"
                    />
                </div>
            }
        >
            <MixBody
                mix={mix}
                isLoading={query.isLoading}
                isSwitching={query.isSwitching}
                isError={query.isError}
                onRetry={() => void query.retry()}
            />
        </OverviewCard>
    );
}
